import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updatePageSEO, SEO_CONFIGS } from '../../utils/seo';

export default function ServicesPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Update SEO
    updatePageSEO(SEO_CONFIGS.services);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold text-[#2D7A4F]">
              おかやま学習塾
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/services" className="text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium whitespace-nowrap border-b-2 border-[#2D7A4F]">事業内容</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">会社概要</Link>
              <Link to="/careers" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">求人情報</Link>
              <Link to="/blog" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">ブログ・新着情報</Link>
              <Link to="/trial" className="text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium whitespace-nowrap">体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-[#2D7A4F] text-white px-6 py-2.5 rounded-full hover:bg-[#236038] transition-colors flex items-center space-x-2 text-base font-medium whitespace-nowrap">
                <i className="ri-line-fill text-lg"></i>
                <span>LINE相談</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#2D7A4F] w-10 h-10 flex items-center justify-center"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-4">
              <Link to="/services" className="block text-[#2D7A4F] hover:text-[#236038] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>事業内容</Link>
              <Link to="/about" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>会社概要</Link>
              <Link to="/careers" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>求人情報</Link>
              <Link to="/blog" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>ブログ・新着情報</Link>
              <Link to="/trial" className="block text-gray-700 hover:text-[#2D7A4F] transition-colors text-base font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>体験申し込み</Link>
              <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="block bg-[#2D7A4F] text-white px-6 py-3 rounded-full hover:bg-[#236038] transition-colors text-center text-base font-medium">
                <i className="ri-line-fill mr-2"></i>LINE相談
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] sm:min-h-[400px] flex items-center justify-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/cfbb8386c77e467557a200a7332b3a1b.jpg" 
            alt="おかやま学習塾の授業風景"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">事業内容</h1>
          <div className="flex items-center justify-center space-x-3 text-white/90 text-sm">
            <Link to="/" className="hover:text-white transition-colors">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>事業内容</span>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              一人ひとりに最適な<br className="sm:hidden" />学習プランを提供
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              岡山市北区の学校事情を熟知した講師陣が、生徒様の学力と目標に合わせた完全個別カリキュラムで指導します。
            </p>
          </div>

          {/* Service Cards */}
          <div className="space-y-16">
            {/* Elementary School */}
            <div className="bg-[#F1F8F4] rounded-3xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-0">
                <div className="order-2 lg:order-1 p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-block bg-[#2D7A4F] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 w-fit whitespace-nowrap">
                    小学生コース
                  </div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                    基礎学力の定着と<br />中学受験対策
                  </h3>
                  <p className="text-base lg:text-lg text-gray-700 leading-relaxed mb-6">
                    岡山市北区の小学校の進度に合わせ、算数・国語・理科・社会の基礎学力をしっかりと定着させます。中学受験を目指す生徒様には、岡山大学教育学部附属中学校、岡山操山中学校などの志望校に合わせた専門的な指導を行います。
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">学校の授業の予習・復習で基礎を固める</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">中学受験に必要な応用力・思考力を養成</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">学習習慣の確立と自主学習の方法を指導</span>
                    </li>
                  </ul>
                </div>
                <div className="order-1 lg:order-2 h-[300px] lg:h-auto">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Japanese%20elementary%20school%20student%20studying%20happily%20with%20textbooks%20and%20notebooks%20in%20bright%20classroom%20focused%20on%20learning%20basic%20subjects%20simple%20clean%20background&width=800&height=600&seq=elementary001&orientation=landscape" 
                    alt="岡山市北区の学習塾で学ぶ小学生"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Junior High School */}
            <div className="bg-[#F1F8F4] rounded-3xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-0">
                <div className="h-[300px] lg:h-auto">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Japanese%20junior%20high%20school%20student%20studying%20intensively%20for%20high%20school%20entrance%20exam%20with%20determination%20focused%20expression%20in%20modern%20classroom%20simple%20clean%20background&width=800&height=600&seq=junior001&orientation=landscape" 
                    alt="岡山市北区の学習塾で高校受験対策をする中学生"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-block bg-[#2D7A4F] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 w-fit whitespace-nowrap">
                    中学生コース
                  </div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                    定期テスト対策と<br />高校受験指導
                  </h3>
                  <p className="text-base lg:text-lg text-gray-700 leading-relaxed mb-6">
                    岡山市北区の中学校の定期テスト対策から、岡山朝日高校、岡山城東高校、岡山芳泉高校などの難関校受験まで、幅広く対応します。内申点アップと入試本番での得点力向上を両立させる指導を行います。
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">定期テスト2週間前からの集中対策</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">岡山県公立高校入試の出題傾向を分析した指導</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">内申点対策と面接・小論文指導</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* High School */}
            <div className="bg-[#F1F8F4] rounded-3xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-0">
                <div className="order-2 lg:order-1 p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-block bg-[#2D7A4F] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 w-fit whitespace-nowrap">
                    高校生コース
                  </div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                    大学受験対策と<br />推薦入試サポート
                  </h3>
                  <p className="text-base lg:text-lg text-gray-700 leading-relaxed mb-6">
                    岡山大学、岡山県立大学などの地元国公立大学から、難関私立大学まで、志望校に合わせた受験対策を実施します。共通テスト対策、二次試験対策、推薦入試の小論文・面接指導まで、総合的にサポートします。
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">共通テスト・二次試験の科目別対策</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">推薦入試の志望理由書・小論文添削</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#2D7A4F] text-xl mr-3 mt-1"></i>
                      <span className="text-base text-gray-700">学習計画の立案と進捗管理</span>
                    </li>
                  </ul>
                </div>
                <div className="order-1 lg:order-2 h-[300px] lg:h-auto">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Japanese%20high%20school%20student%20preparing%20for%20university%20entrance%20exam%20studying%20advanced%20materials%20with%20serious%20concentration%20in%20modern%20study%20environment%20simple%20clean%20background&width=800&height=600&seq=high001&orientation=landscape" 
                    alt="岡山市北区の学習塾で大学受験対策をする高校生"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              おかやま学習塾の特徴
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-user-line text-3xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">完全個別カリキュラム</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                一人ひとりの学力、目標、性格に合わせたオーダーメイドの学習プランを作成。岡山市北区の各学校の進度に完全対応します。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-3xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">地域密着型指導</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                岡山市北区の学校事情、入試傾向を熟知。地域に根ざした6年間の実績で、最適な受験戦略を提案します。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-calendar-check-line text-3xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">柔軟な時間割</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                部活動や習い事との両立が可能な柔軟な時間割設定。振替授業にも対応し、無理なく通塾できます。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">経験豊富な講師陣</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                岡山市北区の入試事情に精通した講師が指導。生徒様一人ひとりに寄り添い、学習意欲を引き出します。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">定期的な学習報告</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                毎月の学習状況レポートで、保護者様にも進捗を共有。三者面談で目標達成までの道筋を明確にします。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-3xl text-[#2D7A4F]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">充実の教材・設備</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                最新の教材と快適な学習環境を完備。自習室も利用可能で、集中して学習に取り組めます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#A5D6A7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            まずは無料体験授業を<br className="sm:hidden" />お試しください
          </h2>
          <p className="text-base text-gray-700 mb-8 sm:mb-10 px-4">
            お子様の学習状況を診断し、<br className="sm:hidden" />最適なプランをご提案します
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <Link to="/trial" className="bg-[#2D7A4F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-[#236038] transition-all text-base lg:text-lg font-semibold flex items-center justify-center space-x-2 shadow-lg whitespace-nowrap">
              <span>無料体験授業を申し込む</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
            <a href="tel:0860123456" className="bg-white text-[#2D7A4F] border-2 border-[#2D7A4F] px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-gray-50 transition-all text-base lg:text-lg font-semibold flex items-center justify-center space-x-2 whitespace-nowrap">
              <i className="ri-phone-fill text-xl"></i>
              <span>086-012-3456</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B5E3F] text-white mx-6 lg:mx-8 my-8 rounded-3xl">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-3xl lg:text-4xl font-light mb-6">お気軽にご相談ください</h3>
              <p className="text-base text-white/80 mb-8 leading-relaxed">
                岡山市北区で志望校合格を目指すなら、おかやま学習塾にお任せください。無料体験授業も随時受付中です。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0860123456" className="text-white text-xl font-semibold hover:text-[#A5D6A7] transition-colors whitespace-nowrap flex items-center">
                  <i className="ri-phone-fill text-xl mr-2"></i>
                  <span>086-012-3456</span>
                </a>
                <a href="https://line.me/R/ti/p/@okayamajuku" target="_blank" rel="noopener noreferrer" className="bg-white text-[#1B5E3F] px-6 py-3 rounded-full hover:bg-white/90 transition-colors text-center font-semibold whitespace-nowrap">
                  <i className="ri-line-fill mr-2"></i>LINEで相談
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">サイトマップ</h4>
              <ul className="space-y-4">
                <li><Link to="/services" className="text-base hover:text-[#A5D6A7] transition-colors underline">事業内容</Link></li>
                <li><Link to="/about" className="text-base hover:text-[#A5D6A7] transition-colors underline">会社概要</Link></li>
                <li><Link to="/careers" className="text-base hover:text-[#A5D6A7] transition-colors underline">求人情報</Link></li>
                <li><Link to="/blog" className="text-base hover:text-[#A5D6A7] transition-colors underline">ブログ・新着情報</Link></li>
                <li><Link to="/trial" className="text-base hover:text-[#A5D6A7] transition-colors underline">体験申し込み</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold mb-6 text-white/70">おかやま学習塾</h4>
              <p className="text-base text-white/80 mb-2">営業時間：15:00 〜 22:00</p>
              <p className="text-base text-white/80 mb-6">定休日：日曜日・祝日</p>
              <h4 className="text-base font-semibold mb-4 text-white/70">所在地</h4>
              <p className="text-base text-white/80">〒700-0000</p>
              <p className="text-base text-white/80">岡山市北区○○町1-2-3</p>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/70 text-sm">
              © 2025 おかやま学習塾 All Rights Reserved.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">プライバシーポリシー</a>
              <a href="#" className="text-white/70 hover:text-white transition-colors underline">特定商取引法</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors underline">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}